import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuthState } from '@/hooks/useAuthState';
import AdminLayout from '@/layouts/AdminLayout';
import { ArrowLeft, Save, Building2, Mail, Phone, Globe, MapPin, Calendar, Camera, Upload, Share2 } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { CountrySelect, Country } from '@/components/CountrySelect';
import InputMask from 'react-input-mask';

// Emojis comuns organizados por categoria
const emojiCategories = {
  'Faces': ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙'],
  'Gestos': ['👋', '🤚', '🖐', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍'],
  'Objetos': ['🏠', '🚗', '✈️', '🚢', '🚲', '🏖️', '🌴', '🌊', '☀️', '🌙', '⭐', '🎉', '🎊', '🎈', '🎁', '🎂', '🍕', '🍔', '🍟', '🍦'],
  'Símbolos': ['✅', '❌', '⚠️', 'ℹ️', '💡', '🔥', '💯', '⭐', '🌟', '✨', '💫', '💥', '💢', '💤', '💨', '💦', '💧', '🌊', '🌈', '☀️'],
  'Natureza': ['🌳', '🌲', '🌴', '🌵', '🌱', '🌿', '🍀', '🍃', '🍂', '🍁', '🌾', '🌷', '🌹', '🌺', '🌻', '🌼', '🌸', '💐', '🌰', '🌍'],
};
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PartnerService } from '@/services/partnerService';
import VoucherManager from '@/components/VoucherManager';
import { getApiEndpoint, getPartnerLogoUrl } from '@/config/api';

interface Partner {
  id: number;
  name: string;
  logo: string;
  address: string;
  website: string;
  email: string;
  phone: string;
  description: string;
  social_media?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const AdminEditPartner = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { isAuthenticated, userType, user, userId, loading: authLoading } = useAuthState();
  const quillRef = useRef<ReactQuill>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  // Determinar se é criação ou edição
  const isCreating = !id || id === 'novo';
  
  // Handler para inserir emoji
  const insertEmoji = (emoji: string) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection();
      const index = range ? range.index : quill.getLength();
      quill.insertText(index, emoji);
      quill.setSelection(index + emoji.length);
    }
    setShowEmojiPicker(false);
  };
  
  // Adicionar botão de emoji ao toolbar após o ReactQuill ser montado
  useEffect(() => {
    let mounted = true;
    
    const addEmojiButton = () => {
      if (!mounted) return;
      
      if (editorContainerRef.current) {
        const toolbar = editorContainerRef.current.querySelector('.ql-toolbar');
        if (toolbar && !toolbar.querySelector('.ql-emoji-button')) {
          const emojiButton = document.createElement('button');
          emojiButton.type = 'button';
          emojiButton.className = 'ql-emoji-button';
          emojiButton.innerHTML = '😊';
          emojiButton.title = 'Inserir emoji';
          emojiButton.setAttribute('aria-label', 'Inserir emoji');
          emojiButtonRef.current = emojiButton;
          emojiButton.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowEmojiPicker((prev) => !prev);
          };
          
          // Adicionar separador e botão ao final do toolbar
          const separator = document.createElement('span');
          separator.className = 'ql-formats';
          separator.appendChild(emojiButton);
          toolbar.appendChild(separator);
        }
      }
    };

    // Tentar adicionar o botão com diferentes delays para garantir que o toolbar foi renderizado
    const timeouts: NodeJS.Timeout[] = [];
    timeouts.push(setTimeout(addEmojiButton, 0));
    timeouts.push(setTimeout(addEmojiButton, 100));
    timeouts.push(setTimeout(addEmojiButton, 300));
    timeouts.push(setTimeout(addEmojiButton, 500));
    
    return () => {
      mounted = false;
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);
  
  // Função para aplicar máscara de telefone brasileiro
  const formatPhoneNumber = (value: string) => {
    // Remove todos os caracteres não numéricos
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a máscara baseada no tamanho
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else if (numbers.length <= 10) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };
  
  const [partnerData, setPartnerData] = useState<Partner | null>(null);
  
  // Inicializar partnerData para modo de criação
  useEffect(() => {
    if (isCreating) {
      setPartnerData({
        id: 0,
        name: '',
        address: '',
        website: '',
        email: '',
        phone: '',
        description: '',
        social_media: '',
        service_type: '',
        logo: '',
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
    }
  }, [isCreating]);
  const [loading, setLoading] = useState(!isCreating);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    website: '',
    email: '',
    phone: '',
    description: '',
    social_media: '',
    service_type: '',
    is_active: true
  });
  const [phoneDdi, setPhoneDdi] = useState('55'); // Brasil como padrão
  const [emailError, setEmailError] = useState<string>('');

  // Verificar autenticação
  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated || userType !== 'admin') {
        navigate('/admin/login');
      }
    }
  }, [isAuthenticated, userType, authLoading, navigate]);

  // Definir loading como false para criação
  useEffect(() => {
    if (isCreating) {
      setLoading(false);
    }
  }, [isCreating]);

  // Carregar dados do parceiro
  useEffect(() => {
    const loadPartner = async () => {
      if (isCreating || !id) return;
      
      try {
        setLoading(true);
        console.log('🔄 Carregando parceiro ID:', id);
        
        const partner = await PartnerService.getPartnerByIdForAdmin(parseInt(id));
        if (!partner) {
          toast({
            title: "Erro",
            description: "Parceiro não encontrado",
            variant: "destructive",
          });
          navigate('/admin/parceiros');
          return;
        }
        
        console.log('✅ Parceiro carregado:', partner);
        console.log('🖼️ Logo do parceiro:', partner.logo);
        setPartnerData(partner);
        setFormData({
          name: partner.name,
          address: partner.address,
          website: partner.website || '',
          email: partner.email || '',
          phone: partner.phone || '',
          description: partner.description || '',
          social_media: partner.social_media || '',
          service_type: partner.service_type || '',
          is_active: partner.is_active
        });
        // Carregar DDI do parceiro ou usar padrão
        setPhoneDdi((partner as any).phone_ddi || '55');
      } catch (error) {
        console.error('Erro ao carregar parceiro:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os dados do parceiro",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadPartner();
  }, [id, isCreating, toast, navigate]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Validação de e-mail
    if (field === 'email') {
      const emailValue = value as string;
      if (emailValue && emailValue.trim() !== '') {
        // Regex para validar e-mail
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailValue)) {
          setEmailError('Por favor, insira um e-mail válido');
        } else {
          setEmailError('');
        }
      } else {
        // E-mail pode ficar vazio
        setEmailError('');
      }
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      console.log('❌ Arquivo não encontrado');
      return;
    }

    // Para modo de criação, apenas armazenar o arquivo
    if (isCreating) {
      setLogoFile(file);
      console.log('📁 Arquivo selecionado para upload posterior:', file.name);
      return;
    }

    // Para modo de edição, fazer upload imediatamente
    if (!id) {
      console.log('❌ ID não encontrado para edição');
      return;
    }

    try {
      setUploadingLogo(true);
      console.log('📤 Fazendo upload da logo:', file.name, 'para parceiro ID:', id);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('partnerId', id);
      
      console.log('📤 Enviando requisição para upload...');
      const response = await fetch(getApiEndpoint('api/upload/partner-logo'), {
        method: 'POST',
        body: formData,
      });
      
      console.log('📤 Resposta recebida:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Erro na resposta:', errorData);
        throw new Error(errorData.error || 'Erro no upload');
      }
      
      const result = await response.json();
      console.log('✅ Upload realizado:', result);
      
      console.log('📤 Atualizando parceiro no banco de dados...');
      // Atualizar o parceiro com a nova logo
      const updateResult = await PartnerService.updatePartner(parseInt(id), {
        logo: result.path
      });
      console.log('✅ Parceiro atualizado no banco:', updateResult);
      
      // Atualizar o estado local
      setPartnerData(prev => {
        const updated = prev ? { ...prev, logo: result.path } : null;
        console.log('✅ Estado local atualizado:', updated);
        return updated;
      });
      
      toast({
        title: "Sucesso",
        description: "Logo enviada com sucesso!",
      });
    } catch (error) {
      console.error('❌ Erro ao fazer upload da logo:', error);
      toast({
        title: "Erro",
        description: "Não foi possível fazer upload da logo",
        variant: "destructive",
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    // Validar e-mail antes de salvar
    if (formData.email && formData.email.trim() !== '' && emailError) {
      toast({
        title: "Erro de validação",
        description: emailError,
        variant: "destructive",
      });
      return;
    }
    
    try {
      setSaving(true);
      if (isCreating) {
        // Criar novo parceiro
        // Preparar dados para envio, incluindo social_media mesmo se vazio
        const createData = {
          ...formData,
          phone_ddi: phoneDdi,
          social_media: formData.social_media || null // Converter string vazia para null
        };
        
        console.log('📤 Dados a serem enviados para criação:', createData);
        const newPartnerId = await PartnerService.createPartner(createData);
        
        // Upload da logo se houver
        if (logoFile && newPartnerId) {
          try {
            await PartnerService.uploadLogo(newPartnerId, logoFile);
          } catch (logoError) {
            console.error('Erro ao fazer upload da logo:', logoError);
            // Não falha a criação do parceiro se a logo falhar
          }
        }
        
        toast({
          title: "Sucesso",
          description: "Parceiro criado com sucesso!",
        });
      } else {
        // Atualizar parceiro existente
        if (!id) return;
        
        // Preparar dados para envio, incluindo social_media mesmo se vazio
        const updateData = {
          ...formData,
          phone_ddi: phoneDdi,
          social_media: formData.social_media || null // Converter string vazia para null
        };
        
        console.log('📤 Dados a serem enviados para atualização:', updateData);
        await PartnerService.updatePartner(parseInt(id), updateData);
        
        // Upload da logo se houver
        if (logoFile) {
          try {
            await PartnerService.uploadLogo(parseInt(id), logoFile);
          } catch (logoError) {
            console.error('Erro ao fazer upload da logo:', logoError);
            // Não falha a atualização se a logo falhar
          }
        }
        
        toast({
          title: "Sucesso",
          description: "Parceiro atualizado com sucesso!",
        });
      }
      
      navigate('/minha-area/admin/parceiros');
    } catch (error) {
      console.error('Erro ao salvar parceiro:', error);
      toast({
        title: "Erro",
        description: isCreating ? "Não foi possível criar o parceiro" : "Não foi possível salvar as alterações",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando dados do parceiro...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!partnerData) {
    return (
      <AdminLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Parceiro não encontrado</h1>
            <Button onClick={() => navigate('/admin/parceiros')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar para Parceiros
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin/parceiros')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  {isCreating ? 'Novo Parceiro' : 'Editar Parceiro'}
                </h1>
                <p className="text-muted-foreground">
                  {isCreating 
                    ? 'Cadastre um novo parceiro no sistema'
                    : `ID: ${partnerData.id} • Criado em ${format(new Date(partnerData.created_at), 'dd/MM/yyyy', { locale: ptBR })}`
                  }
                </p>
    </div>
    </div>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Salvando...' : (isCreating ? 'Criar Parceiro' : 'Salvar Alterações')}
            </Button>
    </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Informações Básicas */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Building2 className="h-5 w-5 mr-2" />
                    Informações do Parceiro
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="name">Nome do Parceiro</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Digite o nome do parceiro"
                    />
    </div>
                  
                  <div>
                    <Label htmlFor="address">Endereço</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      placeholder="Digite o endereço completo"
                    />
    </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        value={formData.website}
                        onChange={(e) => handleInputChange('website', e.target.value)}
                        placeholder="https://exemplo.com"
                        className="w-full"
                      />
    </div>
                    <div>
                      <Label htmlFor="email">E-mail</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="contato@exemplo.com"
                        className={`w-full ${emailError ? 'border-destructive' : ''}`}
                      />
                      {emailError && (
                        <p className="text-sm text-destructive mt-1">{emailError}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">Opcional</p>
    </div>
                    <div>
                      <Label htmlFor="phone">Telefone</Label>
                      <div className="flex gap-2">
                        <CountrySelect
                          value={phoneDdi}
                          onValueChange={(ddi: string, country: Country) => {
                            setPhoneDdi(ddi);
                          }}
                          disabled={saving}
                        />
                        <InputMask
                          mask="(99) 99999-9999"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', formatPhoneNumber(e.target.value))}
                          disabled={saving}
                          className="flex-1"
                        >
                          {(inputProps: any) => (
                            <Input
                              {...inputProps}
                              id="phone"
                              type="tel"
                              placeholder="(81) 99999-9999"
                              maxLength={15}
                              className="w-full flex-1"
                            />
                          )}
                        </InputMask>
                      </div>
    </div>
                    <div>
                      <Label htmlFor="social_media" className="flex items-center gap-2">
                        <Share2 className="h-4 w-4" />
                        Rede Social
                      </Label>
                      <Input
                        id="social_media"
                        value={formData.social_media}
                        onChange={(e) => handleInputChange('social_media', e.target.value)}
                        placeholder="https://instagram.com/exemplo ou https://facebook.com/exemplo"
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground mt-1">URL da rede social (Instagram, Facebook, etc)</p>
    </div>
                    <div>
                      <Label htmlFor="service_type">Tipo de Serviço</Label>
                      <Select
                        value={formData.service_type || "none"}
                        onValueChange={(value) => handleInputChange('service_type', value === "none" ? "" : value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo de serviço" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nenhum</SelectItem>
                          <SelectItem value="Restaurantes">Restaurantes</SelectItem>
                          <SelectItem value="Farmácias">Farmácias</SelectItem>
                          <SelectItem value="Transfer">Transfer</SelectItem>
                          <SelectItem value="Passeios">Passeios</SelectItem>
                          <SelectItem value="Fotógrafos">Fotógrafos</SelectItem>
                          <SelectItem value="Salões de Beleza">Salões de Beleza</SelectItem>
                          <SelectItem value="Massagem">Massagem</SelectItem>
                          <SelectItem value="Aluguel de Equipamentos">Aluguel de Equipamentos</SelectItem>
                          <SelectItem value="Supermercados">Supermercados</SelectItem>
                          <SelectItem value="Padarias">Padarias</SelectItem>
                          <SelectItem value="Disk Bebidas">Disk Bebidas</SelectItem>
                          <SelectItem value="Outros">Outros</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">Categoria do serviço oferecido pelo parceiro</p>
    </div>
    </div>
                  
                  
                  <div>
                    <Label htmlFor="description">Descrição</Label>
                    <div className="border rounded-md relative" ref={editorContainerRef}>
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={formData.description}
                        onChange={(value) => handleInputChange('description', value)}
                        placeholder="Descreva o parceiro e seus serviços..."
                        modules={{
                          toolbar: {
                            container: [
                              ['bold', 'italic', 'underline'],
                              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                              [{ 'align': ['', 'center'] }]
                            ]
                          }
                        }}
                        formats={[
                          'bold', 
                          'italic', 
                          'underline',
                          'list',
                          'bullet',
                          'align'
                        ]}
                        style={{ minHeight: '200px' }}
                      />
                      {/* Seletor de emojis */}
                      {showEmojiPicker && (
                        <div 
                          className="absolute z-50 bg-popover border border-border rounded-md shadow-lg p-4 max-h-96 overflow-y-auto w-80"
                          style={{
                            top: emojiButtonRef.current && editorContainerRef.current 
                              ? `${emojiButtonRef.current.getBoundingClientRect().bottom - editorContainerRef.current.getBoundingClientRect().top + 8}px`
                              : '40px',
                            right: '0px'
                          }}
                        >
                          <div className="space-y-4">
                            {Object.entries(emojiCategories).map(([category, emojis]) => (
                              <div key={category}>
                                <h4 className="text-sm font-semibold mb-2 text-foreground">{category}</h4>
                                <div className="grid grid-cols-10 gap-1">
                                  {emojis.map((emoji, index) => (
                                    <button
                                      key={`${category}-${index}`}
                                      type="button"
                                      onClick={() => insertEmoji(emoji)}
                                      className="text-xl hover:bg-accent rounded p-1 transition-colors cursor-pointer"
                                      title={emoji}
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Overlay para fechar o seletor ao clicar fora */}
                      {showEmojiPicker && (
                        <div 
                          className="fixed inset-0 z-40" 
                          onClick={() => setShowEmojiPicker(false)}
                        />
                      )}
                      <style>{`
                        .ql-toolbar .ql-emoji-button {
                          width: 28px;
                          height: 24px;
                          display: inline-flex;
                          align-items: center;
                          justify-content: center;
                          cursor: pointer;
                          border: none;
                          background: transparent;
                          padding: 2px;
                          font-size: 18px;
                          line-height: 1;
                        }
                        .ql-toolbar .ql-emoji-button:hover {
                          background: hsl(var(--accent));
                          border-radius: 2px;
                        }
                        .ql-toolbar .ql-emoji-button:active {
                          background: hsl(var(--accent));
                        }
                      `}</style>
                    </div>
    </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                    />
                    <Label htmlFor="is_active">Parceiro Ativo</Label>
    </div>
                </CardContent>
              </Card>
    </div>

            {/* Logo do Parceiro */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Camera className="h-5 w-5 mr-2" />
                    Logo do Parceiro
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <div className="w-32 h-32 mx-auto mb-4 rounded-lg bg-muted flex items-center justify-center border-2 border-dashed border-muted-foreground/25">
                      {partnerData.logo ? (
                        <img 
                          src={getPartnerLogoUrl(partnerData.logo)} 
                          alt="Logo do parceiro"
                          className="w-full h-full object-contain rounded-lg"
                          onError={(e) => {
                            console.error('Erro ao carregar logo:', partnerData.logo);
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <Building2 className="h-16 w-16 text-muted-foreground" />
                      )}
    </div>
                    
                    <div className="space-y-2">
                      <input
                        type="file"
                        id="logo-upload"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full"
                        onClick={() => document.getElementById('logo-upload')?.click()}
                        disabled={uploadingLogo}
                      >
                        {uploadingLogo ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            {partnerData.logo ? 'Alterar Logo' : 'Enviar Logo'}
                          </>
                        )}
                      </Button>
                      
                      <p className="text-sm text-muted-foreground">
                        Formatos aceitos: JPG, PNG, SVG
                        <br />
                        Tamanho máximo: 2MB
                      </p>
    </div>
    </div>
                </CardContent>
              </Card>
    </div>
    </div>

          {/* Vouchers - apenas para edição */}
          {!isCreating && (
            <div className="mt-8">
              <VoucherManager 
                partnerId={partnerData.id} 
                partnerName={partnerData.name} 
              />
    </div>
          )}
      </div>
      </div>
    </AdminLayout>
  );
};

export default AdminEditPartner;
